/**
 * CRM 模組路由 — 客戶／產品／客戶訂單／銷貨單。
 *
 * 註冊方式：由 adminRoutes.jsx import 合併至 children。
 * hub／各列表父路由掛 read；create／edit／remark／allocate 等掛對應 mutation。
 * 客戶主檔：`create` 與 `:id/edit` 須在 `:id` 詳情之前。
 * 產品主檔：`create` 與 `:id/edit` 須在 `:id` 詳情之前。
 * 客戶訂單：`create` 須在 `:id` 之前；`:id` 為 layout（無 element），詳情為 index，edit 為子路由。
 * 報價單：list／create／:id（detail index、edit、remark）。
 * 銷貨單：list／create／edit／remark／allocate／sn-select／detail。
 * 舊 path `crm-mgmt/product-repair/*` redirect → `ROUTES.repairRequest`（產品維修已獨立頂層）。
 */
import { Navigate } from 'react-router';

import {
  COST_ESTIMATE_READ_CODES,
  CUSTOMER_ORDER_READ_CODES,
  CUSTOMER_READ_CODES,
  PERM,
  PRODUCT_READ_CODES,
  QUOTATION_READ_CODES,
  SALES_ORDER_READ_CODES,
} from '#/config/permissions';
import { ROUTES } from '#/config/routes';
import RequirePermission from '#/modules/infra/auth/RequirePermission';
import CrmMgmtHubPage from '#/modules/infra/pages/CrmMgmtHubPage';
import CreateEditCustomerPage from '#/modules/crmMgmt/customer/pages/CreateEditCustomerPage';
import CustomerDetailPage from '#/modules/crmMgmt/customer/pages/CustomerDetailPage';
import CustomerListPage from '#/modules/crmMgmt/customer/pages/CustomerListPage';
import CreateEditProductPage from '#/modules/crmMgmt/product/pages/CreateEditProductPage';
import ProductDetailPage from '#/modules/crmMgmt/product/pages/ProductDetailPage';
import ProductListPage from '#/modules/crmMgmt/product/pages/ProductListPage';
import CreateEditCustomerOrderPage from '#/modules/crmMgmt/customerOrder/pages/CreateEditCustomerOrderPage';
import CustomerOrderDetailPage from '#/modules/crmMgmt/customerOrder/pages/CustomerOrderDetailPage';
import CustomerOrderListPage from '#/modules/crmMgmt/customerOrder/pages/CustomerOrderListPage';
import CustomerOrderManufacturingMaterialsPage from '#/modules/crmMgmt/customerOrder/pages/CustomerOrderManufacturingMaterialsPage';
import CustomerOrderRemarkPage from '#/modules/crmMgmt/customerOrder/pages/CustomerOrderRemarkPage';
import CostEstimateDetailPage from '#/modules/crmMgmt/costEstimate/pages/CostEstimateDetailPage';
import CostEstimateListPage from '#/modules/crmMgmt/costEstimate/pages/CostEstimateListPage';
import CreateEditCostEstimatePage from '#/modules/crmMgmt/costEstimate/pages/CreateEditCostEstimatePage';
import CreateEditQuotationPage from '#/modules/crmMgmt/quotation/pages/CreateEditQuotationPage';
import QuotationDetailPage from '#/modules/crmMgmt/quotation/pages/QuotationDetailPage';
import QuotationListPage from '#/modules/crmMgmt/quotation/pages/QuotationListPage';
import QuotationRemarkPage from '#/modules/crmMgmt/quotation/pages/QuotationRemarkPage';
import CreateEditSalesOrderPage from '#/modules/crmMgmt/salesOrder/pages/CreateEditSalesOrderPage';
import CreateSalesOrderPage from '#/modules/crmMgmt/salesOrder/pages/CreateSalesOrderPage';
import SalesOrderAllocatePage from '#/modules/crmMgmt/salesOrder/pages/SalesOrderAllocatePage';
import SalesOrderSnSelectPage from '#/modules/crmMgmt/salesOrder/pages/SalesOrderSnSelectPage';
import SalesOrderDetailPage from '#/modules/crmMgmt/salesOrder/pages/SalesOrderDetailPage';
import SalesOrderListPage from '#/modules/crmMgmt/salesOrder/pages/SalesOrderListPage';
import SalesOrderRemarkPage from '#/modules/crmMgmt/salesOrder/pages/SalesOrderRemarkPage';
import SnSalesWarrantyPage from '#/modules/crmMgmt/salesOrder/pages/SnSalesWarrantyPage';

const CRM_HUB_READ_CODES = [
  ...CUSTOMER_ORDER_READ_CODES,
  ...QUOTATION_READ_CODES,
  ...COST_ESTIMATE_READ_CODES,
  ...SALES_ORDER_READ_CODES,
  ...PRODUCT_READ_CODES,
  ...CUSTOMER_READ_CODES,
];

export const crmRoutes = [
  {
    path: 'crm-mgmt',
    handle: { title: '客戶管理' },
    children: [
      {
        index: true,
        element: (
          <RequirePermission codes={CRM_HUB_READ_CODES} mode="any">
            <CrmMgmtHubPage />
          </RequirePermission>
        ),
      },
      {
        path: 'customer',
        handle: { title: '客戶管理' },
        element: (
          <RequirePermission codes={CUSTOMER_READ_CODES} mode="any" />
        ),
        children: [
          {
            index: true,
            element: <CustomerListPage />,
          },
          {
            path: 'create',
            handle: { title: '新增客戶' },
            element: (
              <RequirePermission code={PERM.CUSTOMER_CREATE}>
                <CreateEditCustomerPage />
              </RequirePermission>
            ),
          },
          {
            path: ':id/edit',
            handle: { title: '編輯客戶' },
            element: (
              <RequirePermission code={PERM.CUSTOMER_UPDATE}>
                <CreateEditCustomerPage />
              </RequirePermission>
            ),
          },
          {
            path: ':id',
            handle: { title: '客戶詳情' },
            element: <CustomerDetailPage />,
          },
        ],
      },
      {
        path: 'product',
        handle: { title: '產品管理' },
        element: (
          <RequirePermission codes={PRODUCT_READ_CODES} mode="any" />
        ),
        children: [
          {
            index: true,
            element: <ProductListPage />,
          },
          {
            path: 'create',
            handle: { title: '新增產品' },
            element: (
              <RequirePermission code={PERM.PRODUCT_CREATE}>
                <CreateEditProductPage />
              </RequirePermission>
            ),
          },
          {
            path: ':id/edit',
            handle: { title: '編輯產品' },
            element: (
              <RequirePermission code={PERM.PRODUCT_UPDATE}>
                <CreateEditProductPage />
              </RequirePermission>
            ),
          },
          {
            path: ':id',
            handle: { title: '產品詳情' },
            element: <ProductDetailPage />,
          },
        ],
      },
      {
        path: 'customer-order',
        handle: { title: '客戶訂單' },
        element: (
          <RequirePermission codes={CUSTOMER_ORDER_READ_CODES} mode="any" />
        ),
        children: [
          {
            index: true,
            element: <CustomerOrderListPage />,
          },
          {
            path: 'create',
            handle: { title: '新增客戶訂單' },
            element: (
              <RequirePermission code={PERM.CUSTOMER_ORDER_CREATE}>
                <CreateEditCustomerOrderPage />
              </RequirePermission>
            ),
          },
          {
            path: ':id',
            handle: { title: '客戶訂單詳情' },
            children: [
              {
                index: true,
                element: <CustomerOrderDetailPage />,
              },
              {
                path: 'edit',
                handle: { title: '編輯客戶訂單' },
                element: (
                  <RequirePermission code={PERM.CUSTOMER_ORDER_UPDATE}>
                    <CreateEditCustomerOrderPage />
                  </RequirePermission>
                ),
              },
              {
                path: 'remark',
                handle: { title: '編輯備註' },
                element: (
                  <RequirePermission code={PERM.CUSTOMER_ORDER_UPDATE}>
                    <CustomerOrderRemarkPage />
                  </RequirePermission>
                ),
              },
              {
                path: 'manufacturing-materials',
                handle: { title: '建立多階層工單' },
                element: (
                  <RequirePermission code={PERM.WORK_ORDER_CREATE}>
                    <CustomerOrderManufacturingMaterialsPage />
                  </RequirePermission>
                ),
              },
            ],
          },
        ],
      },
      {
        path: 'quotation',
        handle: { title: '報價單' },
        element: (
          <RequirePermission codes={QUOTATION_READ_CODES} mode="any" />
        ),
        children: [
          {
            index: true,
            element: <QuotationListPage />,
          },
          {
            path: 'create',
            handle: { title: '新增報價單' },
            element: (
              <RequirePermission code={PERM.QUOTATION_CREATE}>
                <CreateEditQuotationPage />
              </RequirePermission>
            ),
          },
          {
            path: ':id',
            handle: { title: '報價單詳情' },
            children: [
              {
                index: true,
                element: <QuotationDetailPage />,
              },
              {
                path: 'edit',
                handle: { title: '編輯報價單' },
                element: (
                  <RequirePermission code={PERM.QUOTATION_UPDATE}>
                    <CreateEditQuotationPage />
                  </RequirePermission>
                ),
              },
              {
                path: 'remark',
                handle: { title: '編輯內部備註' },
                element: (
                  <RequirePermission code={PERM.QUOTATION_UPDATE}>
                    <QuotationRemarkPage />
                  </RequirePermission>
                ),
              },
            ],
          },
        ],
      },
      {
        path: 'cost-estimate',
        handle: { title: '成本試算' },
        element: (
          <RequirePermission codes={COST_ESTIMATE_READ_CODES} mode="any" />
        ),
        children: [
          {
            index: true,
            element: <CostEstimateListPage />,
          },
          {
            path: 'create',
            handle: { title: '新增成本試算' },
            element: (
              <RequirePermission code={PERM.COST_ESTIMATE_CREATE}>
                <CreateEditCostEstimatePage />
              </RequirePermission>
            ),
          },
          {
            path: ':id',
            handle: { title: '成本試算詳情' },
            children: [
              {
                index: true,
                element: <CostEstimateDetailPage />,
              },
              {
                path: 'edit',
                handle: { title: '編輯成本試算' },
                element: (
                  <RequirePermission code={PERM.COST_ESTIMATE_UPDATE}>
                    <CreateEditCostEstimatePage />
                  </RequirePermission>
                ),
              },
            ],
          },
        ],
      },
      {
        path: 'sales-order',
        handle: { title: '銷貨單' },
        element: (
          <RequirePermission codes={SALES_ORDER_READ_CODES} mode="any" />
        ),
        children: [
          {
            index: true,
            element: <SalesOrderListPage />,
          },
          {
            path: 'create',
            handle: { title: '新增銷貨單' },
            element: (
              <RequirePermission code={PERM.SALES_ORDER_CREATE}>
                <CreateSalesOrderPage />
              </RequirePermission>
            ),
          },
          {
            path: ':id/edit',
            handle: { title: '編輯銷貨單' },
            element: (
              <RequirePermission code={PERM.SALES_ORDER_UPDATE}>
                <CreateEditSalesOrderPage />
              </RequirePermission>
            ),
          },
          {
            path: ':id/remark',
            handle: { title: '編輯備註' },
            element: (
              <RequirePermission code={PERM.SALES_ORDER_UPDATE}>
                <SalesOrderRemarkPage />
              </RequirePermission>
            ),
          },
          {
            path: ':id/allocate',
            handle: { title: '配置庫存' },
            element: (
              <RequirePermission code={PERM.SALES_ORDER_UPDATE}>
                <SalesOrderAllocatePage />
              </RequirePermission>
            ),
          },
          {
            path: ':id/sn-select',
            handle: { title: '選擇序號' },
            element: (
              <RequirePermission code={PERM.SALES_ORDER_UPDATE}>
                <SalesOrderSnSelectPage />
              </RequirePermission>
            ),
          },
          {
            path: ':id',
            handle: { title: '銷貨單詳情' },
            element: <SalesOrderDetailPage />,
          },
        ],
      },
      {
        path: 'sn-sales-warranties',
        handle: { title: '序號直接銷售' },
        element: (
          <RequirePermission codes={SALES_ORDER_READ_CODES} mode="any">
            <SnSalesWarrantyPage />
          </RequirePermission>
        ),
      },
      {
        path: 'product-repair/*',
        element: <Navigate to={ROUTES.repairRequest} replace />,
      },
    ],
  },
];
